import { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Phone, Trash2, Pencil, AlertCircle } from 'lucide-react';
import { FieldError } from '@/components/glitch-ui/field-error';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import { useSavePhoneNumberMutation, useDeletePhoneNumberMutation } from '@/lib/query-options';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import type { CampaignData } from '@/lib/types';

type PhoneNumberRow = { id: number; value: string; call_extension_enabled: boolean };

interface PhoneNumberSectionProps {
  campaign: CampaignData;
  // `disabled` is intentionally not read inside the section now — the hoisted Add/Edit
  // button in advanced-section.tsx owns the read-only/external gating.
  disabled?: boolean;
}

export interface PhoneNumberSectionRef {
  openAdd: () => void;
}

export const PhoneNumberSection = forwardRef<PhoneNumberSectionRef, PhoneNumberSectionProps>(({ campaign }, ref) => {
  const existing = campaign.phone_numbers?.[0] ?? null;
  const { toast } = useToast();
  const refetchCampaign = useRefetchCampaign(campaign.slug);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal form state
  const [modalPhone, setModalPhone] = useState('');
  const [modalCountryCode, setModalCountryCode] = useState('us');
  const [modalEnabled, setModalEnabled] = useState(false);
  // Tell-on-click (#5857): Save stays enabled; a click with no number marks the field.
  const [phoneError, setPhoneError] = useState('');
  // The PhoneInput value always carries the country dial code (e.g. "+1"), so an
  // "empty" field is never an empty string — track the dial code to tell them apart.
  const [modalDialCode, setModalDialCode] = useState('');

  const saveMutation = useSavePhoneNumberMutation();
  const deleteMutation = useDeletePhoneNumberMutation();

  const openModal = () => {
    setModalPhone(existing?.value ?? '');
    setModalCountryCode(existing?.country_code?.toLowerCase() ?? 'us');
    setModalEnabled(existing?.call_extension_enabled ?? false);
    setPhoneError('');
    setIsModalOpen(true);
  };

  const handlePhoneChange = (value: string, meta: { country: { iso2: string; dialCode?: string } }) => {
    setModalPhone(value);
    setModalCountryCode(meta.country.iso2);
    setModalDialCode(meta.country.dialCode ?? '');
    setPhoneError('');
  };

  const handleSave = async () => {
    // A bare dial code ("+1") is not a phone number. Require at least 4 digits of
    // actual national number beyond the dial code (fallback: 5 digits total when
    // the dial code hasn't been observed yet).
    const digits = modalPhone.replace(/\D/g, '');
    const dialDigits = modalDialCode.replace(/\D/g, '');
    const minDigits = dialDigits ? dialDigits.length + 4 : 5;
    if (digits.length < minDigits) {
      setPhoneError(digits.length <= dialDigits.length ? 'Required' : 'Invalid number');
      return;
    }
    try {
      await saveMutation.mutateAsync({
        campaignId: campaign.id,
        phoneNumber: {
          ...(existing?.id ? { id: existing.id } : {}),
          value: modalPhone.trim(),
          country_code: modalCountryCode.toUpperCase(),
          call_extension_enabled: modalEnabled,
        },
      });
      setIsModalOpen(false);
      await refetchCampaign();
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to save phone number',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!existing?.id) return;
    try {
      await deleteMutation.mutateAsync({ campaignId: campaign.id, phoneNumberId: existing.id });
      setIsDeleting(false);
      await refetchCampaign();
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to remove phone number',
        variant: 'destructive',
      });
    }
  };

  useImperativeHandle(ref, () => ({ openAdd: openModal }), [openModal]);

  const columns = useMemo<ColumnDef<PhoneNumberRow>[]>(() => [
    {
      accessorKey: 'value',
      header: 'Phone Number',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <Phone className='h-3.5 w-3.5 text-gray-400 shrink-0' />
          <span className='text-sm text-gray-900'>{row.original.value}</span>
        </div>
      ),
    },
    {
      accessorKey: 'call_extension_enabled',
      header: 'Extension',
      cell: ({ row }) => (
        row.original.call_extension_enabled ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700'>
            Enabled
          </span>
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700'>
            Disabled
          </span>
        )
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: () => (
        <div className='flex justify-end gap-1'>
          <button
            onClick={openModal}
            className='p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors cursor-pointer'
            aria-label='Edit phone number'
          >
            <Pencil className='h-4 w-4' />
          </button>
          <button
            onClick={() => setIsDeleting(true)}
            className='p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer'
            aria-label='Delete phone number'
          >
            <Trash2 className='h-4 w-4' />
          </button>
        </div>
      ),
      meta: { className: 'px-6 text-right' },
    },
  ], [openModal]);

  return (
    <div>
      <DataTable<PhoneNumberRow>
        data={existing ? [existing] : []}
        columns={columns}
        variant='bordered'
        pageSize={999}
        emptyIcon={Phone}
        emptyTitle='No Phone Numbers'
        emptyDescription='Add a phone number to show in ads as a call extension.'
      />

      {/* Add / Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[420px] max-h-[90vh] overflow-y-auto' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{existing ? 'Edit Phone Number' : 'Add Phone Number'}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-2'>
              <Label>Phone Number</Label>
              <div className='flex items-center gap-2'>
                <div
                  style={{
                    '--react-international-phone-height': '2.5rem',
                    '--react-international-phone-border-color': phoneError ? '#ef4444' : '#e5e7eb',
                    '--react-international-phone-border-radius': '0.375rem',
                    '--react-international-phone-background-color': 'transparent',
                    '--react-international-phone-text-color': '#0f172a',
                    '--react-international-phone-font-size': '0.875rem',
                    '--react-international-phone-selected-dropdown-item-background-color': '#f3f4f6',
                  } as React.CSSProperties}
                >
                  <PhoneInput
                    defaultCountry={modalCountryCode as never}
                    value={modalPhone}
                    onChange={handlePhoneChange}
                    inputStyle={{ width: '200px' }}
                    countrySelectorStyleProps={{
                      buttonStyle: { borderRadius: '0.375rem 0 0 0.375rem' },
                    }}
                    autoFocus
                  />
                </div>
                {phoneError && (
                  <span className='flex items-center gap-1.5 min-w-0'>
                    <AlertCircle className='h-4 w-4 text-red-500 shrink-0' />
                    <FieldError message={phoneError} />
                  </span>
                )}
                {existing && (
                  <button
                    type='button'
                    onClick={() => { setIsModalOpen(false); setIsDeleting(true); }}
                    className='p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer shrink-0'
                  >
                    <Trash2 className='h-4 w-4' />
                  </button>
                )}
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <Checkbox
                id='modal-call-extension-enabled'
                checked={modalEnabled}
                onCheckedChange={(checked) => setModalEnabled(!!checked)}
                disabled={!modalPhone.trim()}
              />
              <Label htmlFor='modal-call-extension-enabled' className='font-normal cursor-pointer'>
                Enable as call extension
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsModalOpen(false)} className='leading-none'>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className='bg-[#290BD9] hover:bg-[#2309C5] leading-none'
            >
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConformationModal
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={handleDelete}
        title='Remove Phone Number'
        description='Remove this phone number and its call extension? This action cannot be undone.'
        confirmText='Remove'
        cancelText='Cancel'
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
});
PhoneNumberSection.displayName = 'PhoneNumberSection';
